import { CalendarBlank } from "@phosphor-icons/react";
import { STAGES } from "./constants";

/**
 * Month calendar view of CRM cards by `scheduled_at`. Click on a card chip in a
 * day cell opens the card detail.
 */
export function CalendarView({ cards, cursor, setCursor, onCardClick }) {
  const monthLabel = cursor.toLocaleDateString("es-MX", { month: "long", year: "numeric" });
  const year = cursor.getFullYear();
  const month = cursor.getMonth();
  const first = new Date(year, month, 1);
  const last = new Date(year, month + 1, 0);
  const startWeekday = (first.getDay() + 6) % 7; // make Monday=0
  const totalDays = last.getDate();
  const cells = [];
  for (let i = 0; i < startWeekday; i++) cells.push(null);
  for (let d = 1; d <= totalDays; d++) cells.push(new Date(year, month, d));
  while (cells.length % 7 !== 0) cells.push(null);

  const byDay = {};
  for (const c of cards) {
    const iso = c.scheduled_at || "";
    const day = iso.slice(0, 10);
    if (!day) continue;
    (byDay[day] = byDay[day] || []).push(c);
  }

  const today = new Date();
  const isSameDate = (a, b) => a && b && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
  const isoOf = (d) => new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
  const prevMonth = () => setCursor(new Date(year, month - 1, 1));
  const nextMonth = () => setCursor(new Date(year, month + 1, 1));
  const goToday = () => setCursor(new Date(today.getFullYear(), today.getMonth(), 1));

  const upcoming = [...cards]
    .filter((c) => c.scheduled_at)
    .sort((a, b) => (a.scheduled_at || "").localeCompare(b.scheduled_at || ""))
    .slice(0, 6);

  return (
    <div className="space-y-4" data-testid="crm-calendar-view">
      <div className="flex items-center justify-between flex-wrap gap-2 border border-zinc-200 bg-white px-4 py-2.5 rounded-sm">
        <div className="flex items-center gap-2">
          <button onClick={prevMonth} className="p-1.5 hover:bg-zinc-100 rounded text-zinc-600" data-testid="cal-prev" aria-label="Mes anterior">‹</button>
          <button onClick={goToday} className="text-xs font-mono-data uppercase px-2 py-1 rounded bg-zinc-100 hover:bg-zinc-200" data-testid="cal-today">Hoy</button>
          <button onClick={nextMonth} className="p-1.5 hover:bg-zinc-100 rounded text-zinc-600" data-testid="cal-next" aria-label="Siguiente mes">›</button>
        </div>
        <div className="font-heading text-xl font-bold capitalize">{monthLabel}</div>
        <div className="text-[11px] text-zinc-500">{cards.length} tarjeta{cards.length === 1 ? "" : "s"} programada{cards.length === 1 ? "" : "s"}</div>
      </div>

      <div className="grid grid-cols-7 gap-px bg-zinc-200 border border-zinc-200 rounded-sm overflow-hidden">
        {["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"].map((d) => (
          <div key={d} className="bg-zinc-50 text-[10px] font-mono-data uppercase tracking-wider text-zinc-500 text-center py-1.5">{d}</div>
        ))}
        {cells.map((d, idx) => {
          if (!d) return <div key={`e${idx}`} className="bg-zinc-50 min-h-[88px]" />;
          const dayKey = isoOf(d);
          const dayCards = byDay[dayKey] || [];
          const todayCell = isSameDate(d, today);
          return (
            <div key={dayKey} className={`bg-white min-h-[88px] p-1 flex flex-col gap-1 ${todayCell ? "ring-2 ring-inset ring-[#002FA7]" : ""}`}>
              <div className={`text-[10px] font-mono-data ${todayCell ? "text-[#002FA7] font-bold" : "text-zinc-400"}`}>{d.getDate()}</div>
              <div className="flex flex-col gap-1 overflow-hidden">
                {dayCards.slice(0, 3).map((c) => {
                  const title = c.title || c.client?.display_name || c.client?.name || "Sin nombre";
                  const stageMeta = STAGES.find((s) => s.v === c.stage) || STAGES[0];
                  return (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => onCardClick(c)}
                      className="text-left px-1.5 py-1 rounded-sm text-[10px] font-semibold truncate text-white hover:opacity-90"
                      style={{ background: stageMeta.color }}
                      title={`${title} · ${stageMeta.label}`}
                      data-testid={`cal-card-${c.id}`}
                    >
                      {title}
                    </button>
                  );
                })}
                {dayCards.length > 3 && (
                  <span className="text-[9px] text-zinc-500 px-1">+{dayCards.length - 3} más</span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {upcoming.length > 0 && (
        <section className="border border-zinc-200 bg-white p-4">
          <h3 className="font-heading text-sm font-bold mb-2 inline-flex items-center gap-2"><CalendarBlank size={14} /> Próximas acciones</h3>
          <div className="space-y-1.5">
            {upcoming.map((c) => {
              const title = c.title || c.client?.display_name || c.client?.name || "Sin nombre";
              const dt = new Date(c.scheduled_at);
              const stageMeta = STAGES.find((s) => s.v === c.stage) || STAGES[0];
              const fmt = dt.toLocaleString("es-MX", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
              return (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => onCardClick(c)}
                  className="w-full flex items-center justify-between gap-2 text-left px-3 py-2 border border-zinc-100 rounded-sm hover:bg-zinc-50"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="w-2 h-2 rounded-full shrink-0" style={{ background: stageMeta.color }} />
                    <span className="font-medium text-sm truncate">{title}</span>
                  </div>
                  <span className="text-[11px] font-mono-data text-zinc-500 shrink-0">{fmt}</span>
                </button>
              );
            })}
          </div>
        </section>
      )}
    </div>
  );
}
